import { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography, Sheet } from '@mui/joy';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { LayoutContext } from '../context';
import { APISurveyorArrive } from '../api';
import { compressImage, humanSize } from '../utils/imageCompress';
import { contentFingerprint } from '../home_media_ui/direct';

export default function SurveyorArrivePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { tokenEmployee } = useContext(LayoutContext);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const [error, setError] = useState('');
  const [photoBlob, setPhotoBlob] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [saved, setSaved] = useState(null);
  const submitting = useRef(false);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };
  useEffect(() => {
    if (photoUrl) return undefined;
    let active = true;
    setError('');
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } }, audio: false,
        });
        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (_) {
        if (active) setError('Не удалось открыть камеру. Разрешите доступ или сделайте снимок через кнопку ниже.');
      }
    })();
    return () => { active = false; stopCamera(); };
  }, [photoUrl]);
  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (!video.videoWidth || !video.videoHeight) { setError('Камера ещё запускается. Подождите изображение.'); return; }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      setPhotoBlob(blob);
      setPhotoUrl(URL.createObjectURL(blob));
      stopCamera();
    }, 'image/jpeg', 0.9);
  };

  const retake = () => {
    setPhotoBlob(null);
    setPhotoUrl('');
  };

  // Фолбэк для мобильных/запрета getUserMedia — съёмка через файловый инпут
  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    stopCamera();
    setError('');

    // Сжимаем сразу при выборе: снимок с айфона 3–6 МБ и может быть HEIC.
    // Canvas отдаёт JPEG, поэтому заодно решается проблема формата.
    let prepared;
    try { prepared = await compressImage(file, { fileName: 'arrival.jpg' }); }
    catch (_) { prepared = file; } // Не теряем оригинал неподдерживаемого формата.
    setPhotoBlob(prepared);
    setPhotoUrl(URL.createObjectURL(prepared));
  };

  const submit = async () => {
    if (!photoBlob || submitting.current || saved) return;

    // страховка от «невнятного» 404 — не шлём запрос с битым id.
    // Бэкенд ждёт числовой PK события в /api/surveyor/events/<pk>/arrive/.
    if (!eventId || !/^\d+$/.test(String(eventId))) {
      setError('Не удалось определить событие для фиксации. Откройте событие и нажмите «Отметить прибытие» заново.');
      return;
    }

    submitting.current = true;
    setSending(true);
    setError('');
    setProgress(0);

    try {
      // Ещё один прогон сжатия — на случай, если снимок пришёл из канваса крупным
      const prepared = await compressImage(photoBlob, { fileName: 'arrival.jpg' });

      const uploadId = await contentFingerprint(prepared, `arrival:${eventId}`);
      const accept = result => {
        setSaved(result);
        if (result.transfer_state !== 'pending' && !result.transfer_pending) navigate(-1);
      };
      const res = await APISurveyorArrive(
        eventId, prepared, tokenEmployee, (p) => setProgress(p), uploadId,
      );

      if (res?.status === 'ok') {
        accept(res);
        return;
      }

      // Одна автоматическая повторная попытка при сетевом сбое/таймауте
      if (attempt === 0 && ['network', 'timeout'].includes(res?.code)) {
        setAttempt(1);
        setError('Связь оборвалась, пробуем ещё раз…');
        const retry = await APISurveyorArrive(
          eventId, prepared, tokenEmployee, (p) => setProgress(p), uploadId,
        );
        if (retry?.status === 'ok') { accept(retry); return; }
        setError(`${retry?.detail || 'Не удалось отправить фото прибытия'} (размер файла: ${humanSize(prepared.size)})`);
        return;
      }

      setError(`${res?.detail || 'Не удалось отправить фото прибытия'} (размер файла: ${humanSize(prepared.size)})`);
    } catch (e) {
      console.error('[arrive submit]', e);
      setError('Не удалось подготовить фото. Попробуйте переснять.');
    } finally {
      submitting.current = false;
      setSending(false);
      setProgress(0);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 640, mx: 'auto' }}>
      <Button variant="soft" startDecorator={<ArrowBackRoundedIcon />} onClick={() => navigate(-1)} disabled={sending} sx={{ mb: 2 }}>
        Назад
      </Button>
      <Typography level="h4" sx={{ mb: 0.5 }}>Фиксация прибытия на объект</Typography>
      <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
        Сфотографируйте адрес или вход в помещение и отправьте — так мы зафиксируем прибытие.
      </Typography>

      <Sheet
        variant="soft"
        sx={{
          borderRadius: 'lg', overflow: 'hidden', mb: 2, aspectRatio: '3 / 4',
          display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'black',
        }}
      >
        {!photoUrl ? (
          <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <img src={photoUrl} alt="Фото прибытия" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        )}
      </Sheet>
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {saved && <Alert color="success" sx={{ mb: 2 }} endDecorator={<Button size="sm" color="success" onClick={() => navigate(-1)}>К событию</Button>}>Прибытие зафиксировано. Фото сохранено на основном сервере и будет автоматически перенесено в домашнее хранилище.</Alert>}
      {error && <Typography level="body-sm" color="danger" sx={{ mb: 1 }}>{error}</Typography>}

      {sending && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ height: 6, borderRadius: 6, bgcolor: 'rgba(148,163,184,0.25)', overflow: 'hidden' }}>
            <Box sx={{
              width: `${progress || 5}%`, height: '100%',
              bgcolor: '#16a34a', borderRadius: 6, transition: 'width .2s',
            }} />
          </Box>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
            Отправляем фото… {progress || 0}%
          </Typography>
        </Box>
      )}

      {!photoUrl ? (
        <Stack spacing={1}>
          <Button size="lg" color="primary" startDecorator={<CameraAltRoundedIcon />} onClick={capture}>
            Снять
          </Button>
          <Button component="label" variant="soft" color="neutral">
            Снять/выбрать через камеру телефона
            <input type="file" accept="image/*" capture="environment" hidden onChange={onFilePick} />
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1}>
          <Button variant="soft" color="neutral" startDecorator={<ReplayRoundedIcon />} onClick={retake} sx={{ flex: 1 }} disabled={sending || Boolean(saved)}>
            Переснять
          </Button>
          <Button color="success" startDecorator={<CheckRoundedIcon />} onClick={submit} loading={sending} disabled={Boolean(saved)} sx={{ flex: 2 }}>
            Отправить
          </Button>
        </Stack>
      )}
    </Box>
  );
}